import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { money } from "../lib/helpers";
import { PageHeader, Field, NativeSelect, Empty } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const BLANK = { name: "", category: "", section: "", description: "", duration: 60, prep: 0, cleanup: 0, prices: {}, online_booking: true,
  professional_ids: [], consent_template_id: "", color: "#e6007e", active: true };

function ServiceEditor({ open, onOpenChange, service, onSaved }) {
  const { locations, staff, templates } = useApp();
  const { t } = useT();
  const [f, setF] = useState(BLANK);
  useEffect(() => { if (open) setF({ ...BLANK, ...(service || {}), consent_template_id: service?.consent_template_id || "" }); }, [open, service]);

  const setPrice = (lid, k, v) => {
    const cur = { price: "", deposit: "", ...(f.prices[lid] || {}) };
    setF({ ...f, prices: { ...f.prices, [lid]: { ...cur, [k]: v } } });
  };
  const togglePrice = (lid, on) => {
    const p = { ...f.prices };
    if (on) p[lid] = { price: 0, deposit: 0 }; else delete p[lid];
    setF({ ...f, prices: p });
  };
  const toggleArr = (id) => setF({ ...f, professional_ids: f.professional_ids.includes(id) ? f.professional_ids.filter((x) => x !== id) : [...f.professional_ids, id] });

  const save = async () => {
    const prices = Object.fromEntries(Object.entries(f.prices).map(([k, v]) => [k, { price: parseFloat(v.price) || 0, deposit: parseFloat(v.deposit) || 0,
      tax_rate: v.tax_rate === "" || v.tax_rate == null ? null : parseFloat(v.tax_rate) }]));
    const body = { ...f, prices, duration: +f.duration, prep: +f.prep, cleanup: +f.cleanup, consent_template_id: f.consent_template_id || null };
    delete body.id; delete body.created_at;
    try {
      service?.id ? await api.put(`/services/${service.id}`, body) : await api.post("/services", body);
      toast.success("Service saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="service-editor">
        <DialogHeader><DialogTitle>{service?.id ? t("edit") : t("create")} · {t("service")}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("name")}><Input data-testid="service-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label={t("category")}><Input data-testid="service-category-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></Field>
          <Field label="Section on booking page"><Input data-testid="service-section-input" list="service-sections" placeholder="Aesthetics" value={f.section || ""} onChange={(e) => setF({ ...f, section: e.target.value })} />
            <datalist id="service-sections">{["Aesthetics", "Osteopathy & Physiotherapy", "Massage", "Consultations"].map((s) => <option key={s} value={s} />)}</datalist></Field>
          <Field label={t("description")} className="sm:col-span-2"><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <div className="grid grid-cols-3 gap-3 sm:col-span-2">
            {["duration", "prep", "cleanup"].map((k) => (
              <Field key={k} label={t(k)}><Input data-testid={`service-${k}-input`} type="number" min="0" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>
            ))}
          </div>
          <div className="sm:col-span-2 rounded-[var(--radius)] border p-4 space-y-3">
            <p className="text-sm font-semibold">{t("price")} / {t("deposit")} / VAT % · {t("locations")}</p>
            {locations.map((l) => {
              const on = !!f.prices[l.id];
              return (
                <div key={l.id} className="grid grid-cols-[auto_1fr_90px_90px_76px] items-center gap-2">
                  <Checkbox data-testid={`service-price-toggle-${l.id}`} checked={on} onCheckedChange={(v) => togglePrice(l.id, !!v)} />
                  <span className="text-sm">{l.name} <span className="text-muted-foreground">({l.currency})</span></span>
                  <Input disabled={!on} type="number" step="0.01" placeholder={t("price")} data-testid={`service-price-${l.id}`} value={f.prices[l.id]?.price ?? ""} onChange={(e) => setPrice(l.id, "price", e.target.value)} />
                  <Input disabled={!on} type="number" step="0.01" placeholder={t("deposit")} data-testid={`service-deposit-${l.id}`} value={f.prices[l.id]?.deposit ?? ""} onChange={(e) => setPrice(l.id, "deposit", e.target.value)} />
                  <Input disabled={!on} type="number" step="0.1" min={0} max={100} placeholder="VAT %" title="VAT % (empty = location default)" data-testid={`service-vat-${l.id}`} value={f.prices[l.id]?.tax_rate ?? ""} onChange={(e) => setPrice(l.id, "tax_rate", e.target.value)} />
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground">VAT is included in the price. Leave VAT empty to use the location's default rate (Settings → Invoices).</p>
            <Field label="VAT exemption note (shown on invoices when VAT is 0%)"><Input data-testid="service-tax-note" value={f.tax_note || ""} placeholder="e.g. Exempt from VAT, art. 10 DPR 633/72" onChange={(e) => setF({ ...f, tax_note: e.target.value })} /></Field>
          </div>
          <Field label={t("consent_form")}>
            <NativeSelect testId="service-consent-select" value={f.consent_template_id} onChange={(v) => setF({ ...f, consent_template_id: v })}
              options={[{ value: "", label: t("none") }, ...templates.map((x) => ({ value: x.id, label: x.name }))]} />
          </Field>
          <Field label={t("color")}><Input type="color" className="h-10 p-1" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} /></Field>
          <div className="sm:col-span-2">
            <p className="text-xs text-muted-foreground mb-2">{t("professional")} (empty = all)</p>
            <div className="flex flex-wrap gap-2">
              {staff.filter((s) => ["professional", "owner"].includes(s.role)).map((s) => (
                <button key={s.id} type="button" onClick={() => toggleArr(s.id)} data-testid={`service-pro-${s.id}`}
                  className={`rounded-full border px-3 py-1 text-xs ${f.professional_ids.includes(s.id) ? "bg-primary text-primary-foreground" : ""}`}>{s.name}</button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-3 text-sm"><Switch data-testid="service-online-switch" checked={f.online_booking} onCheckedChange={(v) => setF({ ...f, online_booking: v })} />{t("online_booking")}</label>
          <label className="flex items-center gap-3 text-sm"><Switch data-testid="service-active-switch" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} />{t("active")}</label>
        </div>
        <Button className="rounded-full mt-2" data-testid="service-save-button" disabled={!f.name} onClick={save}>{t("save")}</Button>
      </DialogContent>
    </Dialog>
  );
}

const SECTION_ORDER = ["Aesthetics", "Osteopathy & Physiotherapy", "Massage", "Consultations"];
const rank = (x) => { const i = SECTION_ORDER.indexOf(x); return i < 0 ? 99 : i; };
const amount = (n) => parseFloat((n.match(/(\d+(?:\.\d+)?)\s*(ml|area|hr|min|session)/i) || [])[1]) || 0;
const byName = (a, b) => a.name.replace(/[\d.]+\s*(ml|areas?|hr|mins?|sessions?)/gi, "").localeCompare(b.name.replace(/[\d.]+\s*(ml|areas?|hr|mins?|sessions?)/gi, "")) || amount(a.name) - amount(b.name);

function ServiceCard({ s, onEdit }) {
  const { locations } = useApp();
  const { t, lang } = useT();
  return (
    <button data-testid={`service-card-${s.id}`} onClick={onEdit} className={`lift text-left rounded-[var(--radius)] border bg-card p-5 ${s.active ? "" : "opacity-50"}`}>
      <div className="flex items-center gap-2 mb-3"><span className="h-3 w-3 rounded-full" style={{ background: s.color }} />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{s.category || "—"}</span>
        <span className="ml-auto text-xs text-muted-foreground">{s.duration} {t("min")}</span></div>
      <div className="font-display text-lg font-semibold mb-3">{s.name}</div>
      <div className="space-y-1">
        {locations.filter((l) => s.prices?.[l.id]).map((l) => (
          <div key={l.id} className="flex justify-between text-sm"><span className="text-muted-foreground">{l.name}</span>
            <span className="font-medium">{money(s.prices[l.id].price, l.currency, lang)}{s.prices[l.id].deposit > 0 && <span className="text-xs text-muted-foreground"> · {t("deposit")} {money(s.prices[l.id].deposit, l.currency, lang)}</span>}</span></div>
        ))}
        {!locations.some((l) => s.prices?.[l.id]) && <p className="text-xs text-amber-700">No price set at any location</p>}
      </div>
    </button>
  );
}

export default function Services() {
  const { services, reload } = useApp();
  const { t } = useT();
  const [edit, setEdit] = useState(null);
  const sections = [...new Set(services.map((s) => s.section || "Other"))].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  return (
    <div>
      <PageHeader title={t("services")}><Button className="rounded-full" data-testid="new-service-btn" onClick={() => setEdit({})}>{t("create")}</Button></PageHeader>
      {!services.length && <Empty text={t("no_data")} />}
      <div className="space-y-10">
        {sections.map((sec) => {
          const list = services.filter((s) => (s.section || "Other") === sec);
          const cats = [...new Set(list.map((s) => s.category || "—"))].sort();
          return (
            <section key={sec} data-testid={`services-section-${sec.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <h2 className="text-lg font-semibold mb-4">{sec} <span className="text-sm font-normal text-muted-foreground">· {list.length}</span></h2>
              {cats.map((c) => (
                <div key={c} className="mb-6">
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{c}</p>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {list.filter((s) => (s.category || "—") === c).sort(byName).map((s) => <ServiceCard key={s.id} s={s} onEdit={() => setEdit(s)} />)}
                  </div>
                </div>
              ))}
            </section>
          );
        })}
      </div>
      <ServiceEditor open={!!edit} onOpenChange={(o) => !o && setEdit(null)} service={edit} onSaved={reload} />
    </div>
  );
}
